import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import AppLayout from "../components/app/AppLayout";
import { formatMoney } from "../lib/currency";
import { BarChart3, Sparkles, AlertTriangle, Info, TrendingUp, Users } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";

// Stable references — extracted outside component to avoid re-creating per render.
const CHART_TICK_11 = { fill: "#94a3b8", fontSize: 11 };
const CHART_TICK_10 = { fill: "#94a3b8", fontSize: 10 };
const TOOLTIP_STYLE = { background: "#0f172a", border: "1px solid #334155", borderRadius: 6 };
const LINE_DOT = { fill: "#3b82f6" };

export default function CostAnalyticsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insights, setInsights] = useState([]);

  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)); }, []);

  const askAi = async () => {
    setLoadingInsights(true);
    try {
      const { data } = await api.get("/analytics/insights");
      setInsights(data.insights || []);
    } catch (e) { console.error("Analytics insights fetch failed:", e); }
    setLoadingInsights(false);
  };

  if (!data) return <AppLayout><div className="p-8 text-slate-500">{t("common.loading")}</div></AppLayout>;

  const tot = data.totals;

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-7xl mx-auto" data-testid="analytics-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="label-tech mb-2">{t("analytics.label")}</div>
            <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3"><BarChart3 size={26} className="text-blue-400"/> {t("analytics.title")}</h1>
          </div>
          <button onClick={askAi} disabled={loadingInsights} className="btn-primary px-5 py-3 rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="ai-insights-btn">
            <Sparkles size={16} className={loadingInsights ? "animate-pulse" : ""}/> {loadingInsights ? t("analytics.analyzing") : t("analytics.generateInsights")}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
          <Kpi label={t("analytics.totalQuotes")} value={tot.quote_count}/>
          <Kpi label={t("analytics.totalValue")} value={formatMoney(tot.total_value_eur, "EUR")} accent/>
          <Kpi label={t("analytics.accepted")} value={tot.accepted_count} color="emerald"/>
          <Kpi label={t("analytics.lost")} value={tot.rejected_count} color="red"/>
          <Kpi label={t("analytics.winRate")} value={`${tot.win_rate_pct}%`}/>
          <Kpi label={t("analytics.avgMargin")} value={`${tot.avg_margin_pct}%`}/>
        </div>

        {insights.length > 0 && (
          <div className="mb-8 space-y-3" data-testid="ai-insights-panel">
            <div className="label-tech flex items-center gap-2"><Sparkles size={12} className="text-blue-400"/> AI Insights ({insights.length})</div>
            {insights.map((ins, i) => (
              <div key={ins.id || `${ins.title || "insight"}-${i}`} className={`tech-card p-4 border-l-4 ${
                ins.severity === "alert" ? "border-l-red-500" : ins.severity === "warn" ? "border-l-amber-500" : "border-l-blue-500"
              }`} data-testid={`insight-${i}`}>
                <div className="flex items-start gap-3">
                  {ins.severity === "alert" ? <AlertTriangle size={18} className="text-red-400 flex-shrink-0 mt-0.5"/> : ins.severity === "warn" ? <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5"/> : <Info size={18} className="text-blue-400 flex-shrink-0 mt-0.5"/>}
                  <div className="flex-1">
                    <div className="font-display font-semibold text-slate-100">{ins.title}</div>
                    <div className="text-sm text-slate-400 mt-1">{ins.body}</div>
                    <div className="mt-2 text-xs text-blue-300 flex items-center gap-1"><TrendingUp size={11}/> {ins.action}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="tech-card p-5">
            <div className="label-tech mb-4">{t("analytics.valueByMonth")}</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.by_month}>
                <CartesianGrid stroke="#334155" strokeDasharray="3 3"/>
                <XAxis dataKey="month" tick={CHART_TICK_11}/>
                <YAxis tick={CHART_TICK_11}/>
                <Tooltip contentStyle={TOOLTIP_STYLE}/>
                <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={LINE_DOT}/>
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="tech-card p-5">
            <div className="label-tech mb-4">{t("analytics.valueByMaterial")}</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_material}>
                <CartesianGrid stroke="#334155" strokeDasharray="3 3"/>
                <XAxis dataKey="material" tick={CHART_TICK_10}/>
                <YAxis tick={CHART_TICK_11}/>
                <Tooltip contentStyle={TOOLTIP_STYLE}/>
                <Bar dataKey="value" fill="#3b82f6"/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="tech-card p-5 lg:col-span-2">
            <div className="label-tech mb-4 flex items-center gap-2"><Users size={12}/> {t("analytics.topCustomers")}</div>
            {data.top_customers.length === 0 ? <div className="text-sm text-slate-500">{t("analytics.noCustomerLinks")}</div> : (
              <div className="divide-y divide-slate-800">
                {data.top_customers.map((c, i) => (
                  <div key={c.name} className="py-3 grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-1 font-mono-data text-slate-500 text-sm">#{i+1}</div>
                    <div className="col-span-5 text-slate-100">{c.name}</div>
                    <div className="col-span-2 text-slate-400 text-sm">{c.count} {t("analytics.quotesShort")}</div>
                    <div className="col-span-4 text-right font-mono-data text-blue-400">{formatMoney(c.value, "EUR")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Kpi({ label, value, accent, color }) {
  const cls = accent ? "text-blue-400" : color==="emerald" ? "text-emerald-400" : color==="red" ? "text-red-400" : "text-slate-100";
  return (
    <div className="tech-card p-3">
      <div className="label-tech text-[9px]">{label}</div>
      <div className={`font-mono-data text-xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
